import { useQuery } from '@tanstack/react-query';
import addDays from 'date-fns/addDays';

import { AVAILABILITY_STATUS } from 'reservations/availabilities/constants';
import { fetchAvailabilities } from 'reservations/availabilities/queries/fetchAvailabilities';
import type { GetAvailabilities } from 'reservations/availabilities/types';
import { HTTP_STATUS_CODE_403_UNAUTHORIZED } from 'reservations/base/constants/httpStatusCodes';
import {
  FORCE_REDIRECT_ARGS,
  REDIRECT_WITH_ACCOUNT_DETAILS
} from 'reservations/base/constants/redirects';
import { COMMON_DATETIME_FORMATS } from 'reservations/base/helpers/datetime';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { useWaitlist } from 'reservations/waitlist/providers/WaitlistProvider';
import type { RequestError } from 'utility/restClient';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: Infinity
};

type UseFirstAvailableDateQuery = {
  partySize: number;
  apiKey: string;
  slug: string;
  maxAdvanceDays: number;
  // eslint-disable-next-line unused-imports/no-unused-vars
  handleSuccess: (date: Date | null) => any;
};

function selectFirstAvailableDate(data: GetAvailabilities): Date | null {
  const availabilities = data.availability;
  const firstAvailableNode = availabilities.find(
    availability => availability.status === AVAILABILITY_STATUS.AVAILABLE
  );

  const firstAvailableDate: string | null = firstAvailableNode?.date ?? null;

  if (firstAvailableDate === null) {
    return firstAvailableDate;
  } else {
    const [date, _time] = firstAvailableDate.split('T');
    const [year, month, day] = date
      .split('-')
      .map((part: string) => parseInt(part));

    return new Date(year, month - 1, day);
  }
}

function useFirstAvailableDateQuery({
  partySize,
  apiKey,
  maxAdvanceDays,
  slug,
  handleSuccess
}: UseFirstAvailableDateQuery) {
  const { handleNavigation } = useCustomRouter();
  const { hasActiveOnlineWaitlist } = useWaitlist();

  const fromToday = new Date();
  const untilDate = addDays(fromToday, maxAdvanceDays);

  const beginDateTime = COMMON_DATETIME_FORMATS.YMMdd(fromToday);
  const endDateTime = COMMON_DATETIME_FORMATS.YMMdd(untilDate);

  return useQuery<GetAvailabilities, RequestError, Date | null>({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['bentoboxReservations', 'first-available-date', slug, partySize],
    queryFn: () =>
      fetchAvailabilities({
        apiKey,
        partySize,
        beginDateTime,
        endDateTime,
        includeClosed: false,
        excludeTimeSlots: true
      }),
    enabled: !!slug && !!partySize && !hasActiveOnlineWaitlist,
    select: selectFirstAvailableDate,
    meta: {
      onQuerySuccess: (data: GetAvailabilities) => {
        const date = selectFirstAvailableDate(data);
        handleSuccess(date);
      },
      onQueryError: (error: RequestError) => {
        // istanbul ignore next
        if (error.code === HTTP_STATUS_CODE_403_UNAUTHORIZED) {
          // NOTE: This means that we are force reloading the page
          handleNavigation(...FORCE_REDIRECT_ARGS);
          return;
        }

        handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
      }
    }
  });
}

export { useFirstAvailableDateQuery };
